import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import {
  UserIcon,
  BriefcaseIcon,
  TruckIcon,
  DocumentCheckIcon,
  BanknotesIcon,
} from "@heroicons/react/24/outline";
import { useParams } from "react-router-dom";

// components
import { SectionSubHeader } from "../../components/layouts/section.subheader";

// utilities
import { openAssetKeyNewTab } from "../../utilities";

export const ScreenPartnerDetailsRegistration = () => {

  // hooks
  const dispatch = useDispatch();
  const { account_id = null } = useParams();
  const {
    created,
    status,
    individual,
    application_location,
    payment_account,
    stripe_account_id,
    agreement,
    date_of_birth,
    electrical_competency,
    registration_toolkit,
    driving_license,
    vehicle
  } = useSelector(context => context.partner);
  const { chargeman, electrical_wiring } = electrical_competency;


  return (
    <div className="bg-white rounded-2xl shadow-md">
      
      {/* PERSONAL INFORMATION */}
      <div className="py-8 px-4 border-b border-slate-200">
        <SectionSubHeader title={'Personal Information'} Icon={UserIcon} />
        <div className="grid grid-cols-6 gap-4 divide-x divide-slate-200">
          <div className="px-2 col-span-3">
            <SideKeyValue label={'Name'} value={individual.name} />
            <SideKeyValue label={'Identification Number'} value={individual.identification_number} />
            <SideKeyValue label={'Birthdate'} value={new Date(date_of_birth).toLocaleDateString()} />
            <SideKeyValue 
              label={'Identification Card'} 
              value={
                <div
                  onClick={() => openAssetKeyNewTab(individual.identification_number_image.key)}
                >
                  <p className="underline text-blue-400 hover:cursor-pointer">Click Here</p>
                </div>
              } 
            />
            <SideKeyValue 
              label={'Living Address'} 
              value={
                <div className="flex flex-col">
                  <p>{individual.address.address1}</p>
                  {individual.address.address2 && <p>{individual.address.address2}</p>}
                  <p>{individual.address.poscode}, {individual.address.city}</p>
                  <p>{individual.address.state}</p>
                </div>
              } 
            />
          </div>

          <div className="px-2 col-span-3">
            <SideKeyValue label={'Email'} value={individual.email} />
            <SideKeyValue label={'Phone Number'} value={individual.phone_number} />
            <SideKeyValue label={'Gender'} value={individual.gender} />
            <SideKeyValue label={'Race'} value={individual.race} />
            <SideKeyValue label={'Shirt Size'} value={registration_toolkit.shirt_size} />
          </div>
        </div>
      </div>

      {/* AGREEMENT INFORMATION */}
      <div className="py-8 px-4 border-b border-slate-200">
        <SectionSubHeader title={'Legal Documents'} Icon={DocumentCheckIcon} />
        <div className="grid grid-cols-6 gap-4 divide-x divide-slate-200">
          <div className="px-2 col-span-3">
            <SideKeyValue label={'Service Agreement'} value={new Date(agreement.service).toLocaleString()} />
            <SideKeyValue label={'PDPA'} value={new Date(agreement.pdpa).toLocaleString()} />
            <SideKeyValue label={'Declaration'} value={new Date(agreement.declaration).toLocaleString()} />
          </div>

          <div className="px-2 col-span-3">
          </div>
        </div>
      </div>

      {/* WORKING EXPERIENCE */}
      <div className="py-8 px-4 border-b border-slate-200">
        <SectionSubHeader title={'Working Experience'} Icon={BriefcaseIcon} />
        <div className="grid grid-cols-6 gap-4 divide-x divide-slate-200">
          <div className="px-2 col-span-3">
            <SideKeyValue label={'Type'} value={'Electrical Wiring Competency'} />
            <SideKeyValue 
              label={'Highest Level Certification'} 
              value={electrical_wiring.not_applicable ? 'Not certified' : electrical_wiring.highest_level} 
            />
            {electrical_wiring.years_of_experience && 
              <SideKeyValue 
                label={'Years of Experience'} 
                value={electrical_wiring.years_of_experience.replace(/_/gi, ' ')} 
              />
            }
            <SideKeyValue 
              label={'Certificate'} 
              value={
                <div
                  onClick={() => openAssetKeyNewTab(electrical_wiring.file.key)}
                >
                  <p className="underline text-blue-400 hover:cursor-pointer">Click Here</p>
                </div>
              } 
            />
          </div>

          <div className="px-2 col-span-3">
            <SideKeyValue label={'Type'} value={'Chargeman Competency'} />
            <SideKeyValue 
              label={'Highest Level Certification'} 
              value={chargeman.not_applicable ? 'Not certified' : chargeman.highest_level} 
            />
            {chargeman.years_of_experience && 
              <SideKeyValue 
                label={'Years of Experience'} 
                value={chargeman.years_of_experience.replace(/_/gi, ' ')} 
              />
            }
            <SideKeyValue 
              label={'Certificate'} 
              value={
                <div
                  onClick={() => openAssetKeyNewTab(chargeman.file.key)}
                >
                  <p className="underline text-blue-400 hover:cursor-pointer">Click Here</p>
                </div>
              } 
            />
          </div>
        </div>
      </div>

      {/* DRIVING */}
      <div className="py-8 px-4 border-b border-slate-200">
        <SectionSubHeader title={'Driving Eligibility'} Icon={TruckIcon} />
        <div className="grid grid-cols-6 gap-4 divide-x divide-slate-200">
          <div className="px-2 col-span-3">
            <SideKeyValue label={'Section'} value={'Registered Vehicle'} />
            <SideKeyValue label={'Type'} value={vehicle.type} />
            <SideKeyValue label={'Plate Number'} value={vehicle.registration_number} />
            <SideKeyValue label={'Road Tax Expired Date'} value={new Date(vehicle.roadtax.expiry_date).toLocaleDateString()} />
            <SideKeyValue 
              label={'Road Tax'} 
              value={
                <div
                  onClick={() => openAssetKeyNewTab(vehicle.roadtax.image.key)}
                >
                  <p className="underline text-blue-400 hover:cursor-pointer">Click Here</p>
                </div>
              } 
            />
          </div>

          <div className="px-2 col-span-3">
            <SideKeyValue label={'Section'} value={'Driving License'} />
            <SideKeyValue label={'Expired Date'} value={new Date(driving_license.expiry_date).toLocaleDateString()} />
            <SideKeyValue 
              label={'License Certificate'} 
              value={
                <div
                  onClick={() => openAssetKeyNewTab(driving_license.image.key)}
                >
                  <p className="underline text-blue-400 hover:cursor-pointer">Click Here</p>
                </div>
              } 
            />
          </div>
        </div>
      </div>

      {/* BANK */}
      <div className="py-8 px-4 border-b border-slate-200">
        <SectionSubHeader title={'Banking Information'} Icon={BanknotesIcon} />
        <div className="grid grid-cols-6 gap-4 divide-x divide-slate-200">
          <div className="px-2 col-span-3">
            <SideKeyValue label={'Bank Name'} value={payment_account.bank_name} />
            <SideKeyValue label={'Account Number'} value={payment_account.account_number} />
            <SideKeyValue label={'Account Holder name'} value={payment_account.name} />
            <SideKeyValue 
              label={'Bank Statement'} 
              value={
                <div
                  onClick={() => openAssetKeyNewTab(payment_account.bank_statement_image.key)}
                >
                  <p className="underline text-blue-400 hover:cursor-pointer">Click Here</p>
                </div>
              } 
            />
          </div>

          <div className="px-2 col-span-3">
            <SideKeyValue label={'Stripe ID'} value={stripe_account_id} />
          </div>
        </div>
      </div>


    </div>
  );
};

const SideKeyValue = ({label, value}) => {
  return (
    <div className="pt-4 grid grid-cols-8">
      <h4 className="col-span-4 text-sm text-slate-500 font-normal">{label}</h4>
      <span className="col-span-4 text-sm text-slate-900 font-medium capitalize text-left">{value}</span>
    </div>
  )
}